import React from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import DailyCalendar from './DailyCalendar';
import TimeBasedPerformance from './TimeBasedPerformance';
import { getChronologicallyIndexedTrades } from '../utils/timeBasedPerformance';
import './TradingPerformance.css';

ChartJS.register(ArcElement, Tooltip, Legend);

function TradingPerformance({ tradesData }) {
  if (!tradesData || !tradesData.completedTrades) {
    return (
      <div className="trading-performance">
        <div className="card">
          <p>No trade data available. Please upload a trade file first.</p>
        </div>
      </div>
    );
  }

  const completedTrades = tradesData.completedTrades || [];
  const openTrades = tradesData.openTrades || [];
  const stats = tradesData.stats || {};
  const chronologicallyIndexedCompletedTrades = React.useMemo(
    () => getChronologicallyIndexedTrades(completedTrades),
    [completedTrades]
  );

  const wins = completedTrades.filter(t => t.status === 'WIN').length;
  const losses = completedTrades.filter(t => t.status === 'LOSS').length;
  const breakEven = completedTrades.filter(t => t.status === 'BREAK_EVEN').length;
  const winRate = stats.winRate || 0;

  const pieData = {
    labels: [
      `Wins (${wins})`,
      `Losses (${losses})`,
      `Break Even (${breakEven})`
    ],
    datasets: [
      {
        data: [wins, losses, breakEven],
        backgroundColor: ['#00ff88', '#ff4757', '#ffd93d'],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const pieOptions = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: {
            size: 13,
            weight: '500'
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = wins + losses + breakEven;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label.replace(/ \(\d+\)/g, '')}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  return (
    <div className="trading-performance">
      <h2>Trading Performance</h2>

      {/* Stats and Chart Side by Side */}
      <div className="stats-chart-container">
        {/* Stats Cards - Stacked on Left */}
        <div className="stats-stack">
          <div className="stat-card-compact">
            <div className="stat-value-compact wins">{wins}</div>
            <div className="stat-label-compact">Winning Trades</div>
          </div>
          <div className="stat-card-compact">
            <div className="stat-value-compact losses">{losses}</div>
            <div className="stat-label-compact">Losing Trades</div>
          </div>
          <div className="stat-card-compact">
            <div className="stat-value-compact break-even">{breakEven}</div>
            <div className="stat-label-compact">Break-Even</div>
          </div>
          <div className="stat-card-compact">
            <div className="stat-value-compact win-rate">{winRate.toFixed(1)}%</div>
            <div className="stat-label-compact">Win Rate</div>
          </div>
        </div>

        {/* Pie Chart - On Right */}
        <div className="card chart-card">
          <h3>Trade Distribution</h3>
          <div className="chart-container-large">
            <Pie data={pieData} options={pieOptions} />
          </div>
        </div>
      </div>

      <TimeBasedPerformance completedTrades={completedTrades} />

      {/* Daily Calendar */}
      <div className="card">
        <h3>Daily P&L Calendar</h3>
        <DailyCalendar completedTrades={completedTrades} />
      </div>

      {/* Completed Trades Table */}
      <div className="card">
        <h3>Completed Trades</h3>
        {completedTrades.length > 0 ? (
          <div className="table-container">
            <table className="trades-table">
              <thead>
                <tr>
                  <th>Idx</th>
                  <th>Date</th>
                  <th>Symbol</th>
                  <th>Avg Buy</th>
                  <th>Avg Sell</th>
                  <th>Quantity</th>
                  <th>P/L</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {chronologicallyIndexedCompletedTrades.map((trade) => (
                  <tr key={`${trade.symbol}-${trade.chronologicalIdx}-${trade.tradeDate || 'no-date'}`}>
                    <td>{trade.chronologicalIdx}</td>
                    <td>{trade.tradeDate || '-'}</td>
                    <td>{trade.symbol}</td>
                    <td>₹{trade.avgBuyPrice.toFixed(2)}</td>
                    <td>₹{trade.avgSellPrice.toFixed(2)}</td>
                    <td>{trade.quantity}</td>
                    <td className={trade.pnl >= 0 ? 'profit' : 'loss'}>
                      ₹{trade.pnl.toFixed(2)}
                    </td>
                    <td>
                      <span className={`badge badge-${trade.status.toLowerCase().replace('_', '-')}`}>
                        {trade.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No completed trades yet.</p>
        )}
      </div>

      {/* Open Trades */}
      {openTrades.length > 0 && (
        <div className="card">
          <h3>Open Positions</h3>
          <div className="table-container">
            <table className="trades-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Position</th>
                  <th>Quantity</th>
                  <th>Avg Price</th>
                  <th>Invested Value</th>
                </tr>
              </thead>
              <tbody>
                {openTrades.map((trade, index) => (
                  <tr key={index}>
                    <td>{trade.symbol}</td>
                    <td>
                      <span className={`badge badge-${trade.position.toLowerCase()}`}>
                        {trade.position}
                      </span>
                    </td>
                    <td>{trade.quantity}</td>
                    <td>₹{trade.avgPrice.toFixed(2)}</td>
                    <td>₹{trade.investedValue.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default TradingPerformance;
